import type {
  AgAreaSeriesOptions,
  AgBarSeriesOptions,
  AgChartOptions,
  AgDonutSeriesOptions,
  AgLineSeriesOptions,
  AgPieSeriesOptions,
  AgScatterSeriesOptions
} from 'ag-charts-community';
import {
  AreaSeries,
  BarSeries,
  Chart,
  ChartSeries,
  DonutSeries,
  LineSeries,
  PieSeries,
  ScatterSeries
} from '../types/chart';
import { ChartAxis } from '../types/axis';
import {
  filterEmpty,
  isResource,
  Path,
  Query,
  QueryBuilder,
  QueryFilter, QuerySort,
  resourceToPromise,
  toIsoDate, toIsoDateTime
} from '@softline/core';
import type {
  AgCartesianAxisOptions,
  AgDataSourceCallbackParams,
  AgDataSourceOptions,
  AgUnitTimeAxisOptions,
  Formatter as AgFormatter
} from 'ag-charts-enterprise';
import { Injector, isSignal } from '@angular/core';
import { DataSource, isQueryDataSource } from '@softapps/core';
import { AG_CHARTS_LOCALE_DE_DE } from 'ag-charts-locale';
import { formatter, Formatter } from '@softline/ui-core';
import { AppearanceService } from '@softline/application';

type AgSeriesOptions =
  | AgBarSeriesOptions
  | AgLineSeriesOptions
  | AgAreaSeriesOptions
  | AgScatterSeriesOptions
  | AgPieSeriesOptions
  | AgDonutSeriesOptions;

export class ChartDataCache {
  request: object | null = null;
  data: object[] | null = null;
}

export function toAgChartOptions<T extends object>(chart: Chart<T>, options: {formatter: Formatter, injector?: Injector, cache: ChartDataCache, appearanceService: AppearanceService }): AgChartOptions {
  const series = chart.series();
  const agSeries = series.map((s) => toAgSeries(s));

  const axes = chart.axes();
  const agAxes = {};
  const agFormatter = {};
  for (const axis of axes) {
    agAxes[axis.name] = toAgAxis(axis, options.formatter);
    agFormatter[axis.name] = toAgFormatter(axis, options.formatter);
  }

  const filter = chart.filter();
  const sort = chart.sort();
  const data = toAgData(chart.dataSource, series, axes, filter, sort, options);
  const result = {
    ...data,
    theme: options.appearanceService.isDarkMode() ? 'ag-default-dark' : 'ag-default' ,
    series: agSeries,
    axes: agAxes,
    locale: {
      localeText: AG_CHARTS_LOCALE_DE_DE,
    },
    formatter: agFormatter
  } as AgChartOptions;

  if (chart.title)
    result.title = { text: chart.title() };

  if (chart.legend === false)
    result.legend = { enabled: false };

  if(chart.zoom) {
    result.zoom = {
      enabled: true,
      buttons: { enabled: false },
    };
    result.navigator = { enabled: true };
    if(axes.find(o => o.type === 'time'))
      result.ranges = { enabled: true };
  }

  return result;
}

function toAgData<T extends object>(dataSource: DataSource<T>,
                                    series: ChartSeries<T>[],
                                    axes: ChartAxis[],
                                    filter: QueryFilter<T>[],
                                    sort: QuerySort<T>[],
                                    options: { injector?: Injector, cache: ChartDataCache }): {data?: object[], dataSource?: AgDataSourceOptions<object>} {
  if (isQueryDataSource(dataSource)) {
    return {
      dataSource: {
        getData: async (o) => {
          const query = seriesToQuery(series, axes, filter, sort, o);
          if(options.cache.request && options.cache.data && JSON.stringify(query) === JSON.stringify(options.cache.request))
            return options.cache.data;
          const result = await dataSource.load({ query });
          const dataValues = toAgDataValues(filterEmpty(result.data), series as any, axes);
          options.cache.request = query;
          options.cache.data = dataValues;
          return dataValues;
        }
      }
    }
  }
  if (isSignal(dataSource))
    return { data: dataSource().map(o => flattenObject(o)) };
  if (isResource(dataSource)) {
    if(dataSource.status() === 'resolved' || dataSource.status() === 'local')
      return { data: (dataSource.value() ?? []).map(o => flattenObject(o)) };
    return {
      dataSource: {
        getData: async (o) => ((await resourceToPromise(dataSource,  options)) ?? []).map(o => flattenObject(o))
      }
    }
  }
  if (Array.isArray(dataSource))
    return {data: dataSource.map(o => flattenObject(o))};
  return { data: [] };
}

function seriesToQuery<T extends object>(series: ChartSeries<T>[],
                                         axes: ChartAxis[],
                                         filter: QueryFilter<T>[],
                                         sort: QuerySort<T>[],
                                         options: AgDataSourceCallbackParams): Query<T> {
  const query = new QueryBuilder<T>()
    .end(100_000);
  let restrictionName: string | null = null;
  for (const a of axes) {
    if(a.type === 'time') {
      restrictionName = a.name
      continue;
    }
  }
  let restrictionAxis: string | null = null;
  for (const s of series) {
    switch (s.type){
      case 'bar':
      case 'line':
      case 'area':
      case 'scatter':
        query.column(s.xKey, { func: s.xFunction as any, agg: s.xAggregation as any });
        query.column(s.yKey, { func: s.yFunction as any, agg: s.yAggregation as any });
        if (restrictionName && (restrictionName === 'x' || restrictionName === s.xAxis?.name))
          restrictionAxis = s.xKey;
        else if (restrictionName && (restrictionName === 'y' || restrictionName === s.yAxis?.name))
          restrictionAxis = s.yKey;
        break;
    }
  }
  if(restrictionAxis && options.windowStart){
    query.filter({
      name: restrictionAxis as Path<T>,
      type: 'dateAfterOrEquals',
      value: toIsoDate(options.windowStart)
    });
  }
  if(restrictionAxis && options.windowEnd) {
    query.filter({
      name: restrictionAxis as Path<T>,
      type: 'dateBeforeOrEquals',
      value: toIsoDate(options.windowEnd)
    });
  }
  if(filter) {
    for (const f of filter)
      query.filter(f)
  }
  if(sort) {
    for (const s of sort)
      query.sort(s.name, s.direction)
  }
  return query.build();
}

function toAgAxis(axis: ChartAxis, formatter: Formatter): AgCartesianAxisOptions {
  const agAxis: AgCartesianAxisOptions = {
    title: axis.title ? { text: axis.title() } : undefined,
    position: axis.position,
  }

  switch (axis.type) {
    case 'category':
    case 'grouped-category':
    case 'number':
    case 'log':
      agAxis.type = axis.type;
      agAxis.label = axis.labelFormatter ? {
        formatter: o => (axis.labelFormatter ? (axis as any).labelFormatter(o.value) : '')
      }: undefined
      break;
    case 'amount':
    case 'quantity':
      agAxis.type = 'number';
      agAxis.label = axis.labelFormatter ? {
        formatter: o => (axis.labelFormatter ? axis.labelFormatter(o.value) : '')
      }: undefined
      break;
    case 'time':
      agAxis.type = 'time';
      if(axis.mode === 'ordinal')
        agAxis.type = 'ordinal-time';
      if (axis.unit) {
        agAxis.type  = 'unit-time';
        (agAxis as AgUnitTimeAxisOptions<any>).unit = axis.unit;
      }
      agAxis.label = {
        formatter: o => {
          if(axis.labelFormatter)
            return axis.labelFormatter(toIsoDateTime(o.value))
          switch (o.unit) {
            case 'year':
              return formatter(toIsoDateTime(o.value), 'DATE.YEAR_ONLY')()
            case 'month':
              return formatter(toIsoDateTime(o.value), 'DATE.MONTH_ONLY_SHORT')()
            case 'day':
              return formatter(toIsoDateTime(o.value), 'DATE.DAY_ONLY')()
          }
          return '';
        }
      }
      if(axis.labelFormatter)
        agAxis.label = axis.labelFormatter ? {
          formatter: (o) => {
            return axis.labelFormatter ? axis.labelFormatter(toIsoDateTime(o.value)) : '';
          }
        }: undefined;
      (agAxis as any).parentLevel = {
        enabled: true,
        label: {
          formatter: o => {
            if(axis.parentFormatter)
              return axis.parentFormatter(toIsoDateTime(o.value), o.unit)
            switch (o.unit) {
              case 'year':
                return formatter(toIsoDateTime(o.value), 'DATE.YEAR_ONLY')()
              case 'month':
                return formatter(toIsoDateTime(o.value), 'DATE.MONTH_ONLY_SHORT')()
              case 'day':
                return formatter(toIsoDateTime(o.value), 'DATE.DAY_ONLY')()
            }
            return '';
          }
        }
      }
      break;
    default:
      throw new Error('[toAgChartOptions - toAgAxis] Invalid axis axis axis');
  }
  return agAxis;
}

function toAgFormatter(axis: ChartAxis, formatter: Formatter): AgFormatter<any> | undefined {
  switch (axis.type) {
    case 'category':
    case 'grouped-category':
    case 'number':
    case 'log':
    case 'amount':
    case 'quantity':
      return axis.formatter ? o => (axis.formatter ? (axis as any).formatter(o.value) : undefined) : undefined;
    case 'time':
      return (o) => {
        if(axis.formatter)
          return axis.formatter(toIsoDateTime(o.value))
        return formatter(toIsoDateTime(o.value), 'DATE.DEFAULT')()
      }
    default:
      throw new Error('[toAgChartOptions - toAgAxis] Invalid axis axis axis');
  }
}

function toAgSeries<T extends object>(s: ChartSeries<T>): AgSeriesOptions {
  switch (s.type) {
    case 'bar':
      return toBarSeries(s);
    case 'line':
      return toLineSeries(s);
    case 'area':
      return toAreaSeries(s);
    case 'scatter':
      return toScatterSeries(s);
    case 'pie':
      return toPieSeries(s);
    case 'donut':
      return toDonutSeries(s);
  }
}

function toBarSeries<T extends object>(s: BarSeries<T>): AgBarSeriesOptions {
  return {
    type: 'bar',
    xKey: getFlatternedQueryName(s.xKey, s.xAggregation, s.xFunction),
    yKey: getFlatternedQueryName(s.yKey, s.yAggregation, s.yFunction),
    yName: s.label() as string,
    stacked: s.stacked,
    grouped: s.grouped,
    stackGroup: s.stackGroup,
    direction: s.direction,
    xKeyAxis: s.xAxis?.name,
    yKeyAxis: s.yAxis?.name,
    tooltip: s.tooltip ? {
      renderer: (o) => s.tooltip ? s.tooltip(unflattenObject(o.datum) as T) : ''
    }: undefined,
    itemStyler: (o: any) => (s.styler && o.datum) ? s.styler(o.datum, o) : { fill: o.fill },
  };
}

function toLineSeries<T extends object>(s: LineSeries<T>): AgLineSeriesOptions {
  return {
    type: 'line',
    xKey: getFlatternedQueryName(s.xKey, s.xAggregation, s.xFunction),
    yKey: getFlatternedQueryName(s.yKey, s.yAggregation, s.yFunction),
    yName: s.label() as string,
    xKeyAxis: s.xAxis?.name,
    yKeyAxis: s.yAxis?.name,
    tooltip: s.tooltip ? {
      renderer: (o) => s.tooltip ? s.tooltip(unflattenObject(o.datum) as T) : ''
    }: undefined,
  };
}

function toAreaSeries<T extends object>(s: AreaSeries<T>): AgAreaSeriesOptions {
  return {
    type: 'area',
    xKey: getFlatternedQueryName(s.xKey, s.xAggregation, s.xFunction),
    yKey: getFlatternedQueryName(s.yKey, s.yAggregation, s.yFunction),
    yName: s.label() as string,
    stacked: s.stacked,
    xKeyAxis: s.xAxis?.name,
    yKeyAxis: s.yAxis?.name,
    tooltip: s.tooltip ? {
      renderer: (o) => s.tooltip ? s.tooltip(unflattenObject(o.datum) as T) : ''
    }: undefined,
  };
}

function toScatterSeries<T extends object>(s: ScatterSeries<T>): AgScatterSeriesOptions {
  return {
    type: 'scatter',
    xKey: getFlatternedQueryName(s.xKey, s.xAggregation, s.xFunction),
    yKey: getFlatternedQueryName(s.yKey, s.yAggregation, s.yFunction),
    xKeyAxis: s.xAxis?.name,
    yKeyAxis: s.yAxis?.name,
    tooltip: s.tooltip ? {
      renderer: (o) => s.tooltip ? s.tooltip(unflattenObject(o.datum) as T) : ''
    }: undefined,
  };
}

function toPieSeries<T extends object>(s: PieSeries<T>): AgPieSeriesOptions {
  return {
    type: 'pie',
    angleKey: s.angleKey,
    radiusKey: s.radiusKey,
    outerRadiusRatio: s.outerRadiusRatio,
    tooltip: s.tooltip ? {
      renderer: (o) => s.tooltip ? s.tooltip(unflattenObject(o.datum) as T) : ''
    }: undefined,
  };
}

function toDonutSeries<T extends object>(s: DonutSeries<T>): AgDonutSeriesOptions {
  return {
    type: 'donut',
    angleKey: s.angleKey,
    radiusKey: s.radiusKey,
    outerRadiusRatio: s.outerRadiusRatio,
    innerRadiusRatio: s.innerRadiusRatio,
    tooltip: s.tooltip ? {
      renderer: (o) => s.tooltip ? s.tooltip(unflattenObject(o.datum) as T) : ''
    }: undefined,
  };
}

/**
 * Recursively flattens a plain object into a single-level map of dot-notation
 * keys. Arrays, `Date` instances and non-plain values are treated as leaves.
 */
function flattenObject(value: object, prefix = ''): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value)) {
    const path = prefix ? `${prefix}__${key}` : key;
    if (
      val !== null &&
      typeof val === 'object' &&
      !Array.isArray(val) &&
      !(val instanceof Date)
    ) {
      Object.assign(result, flattenObject(val, path));
    } else {
      result[path] = val;
    }
  }
  return result;
}

/**
 * Reverses `flattenObject`: expands a single-level map of `__`-separated keys
 * back into a nested object. Leaf values (arrays, `Date`s, primitives) are
 * assigned as-is.
 */
function unflattenObject(flat: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [path, value] of Object.entries(flat)) {
    const keys = path.split('__');
    let node = result;
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const existing = node[key];
      if (existing === null || typeof existing !== 'object' || Array.isArray(existing)) {
        node[key] = {};
      }
      node = node[key] as Record<string, unknown>;
    }
    node[keys[keys.length - 1]] = value;
  }
  return result;
}

function reconstructFlatternedObject(value: object): any {

}

function toAgDataValues(values: object[], series: ChartSeries<object>[], axes: ChartAxis[]): Record<string, unknown>[] {
  const transformDateValueKeys: string[] = [];
  for (const a of axes) {
    if(a.type === 'time')
      for (const s of series) {
        switch (s.type){
          case 'bar':
          case 'line':
          case 'area':
          case 'scatter':
            if (a.name && (a.name === 'x' || a.name === s.xAxis?.name))
              transformDateValueKeys.push(getFlatternedQueryName(s.xKey));
            else if (a.name && (a.name === 'y' || a.name === s.yAxis?.name))
              transformDateValueKeys.push(getFlatternedQueryName(s.yKey));
            break;
        }
      }
  }
  let returnValue = values.map(o => flattenObject(o));
  for (const key of transformDateValueKeys)
    returnValue = returnValue.map(o => ({...o, [key]: new Date(o[key] as string)}))
  return returnValue;
}

function getFlatternedQueryName(key: string, agg?: string, func?: string): string {
  return `${key.replace('.', '__')}${func ? '|' + func : ''}${agg ? ':' + agg : ''}`
}
